import { useEffect, useState } from "react"
import { Form, Button } from "react-bootstrap"
import toast from "react-hot-toast"

import { useUpdateUserMutation } from "../../../app/userApiSlice"
import {
  isValidName,
  isValidAddress,
  isValidPostcode,
  isValidCountry,
  isValidDob,
  isValidCityOrCounty,
  isValidPhoneNumber,
} from "../../../utils/validateInput"
import ControlInput from "../../../components/form-inputs/UpdateControlInput"
import useAuth from "../../../hooks/useAuth"
import LoadingTextSwap from "../../../components/LoadingTextSwap"
import ButtonWithTooltip from "../../../components/buttons/ButtonWithTooltip"
import { Edit } from "lucide-react"

const UpdateUserForm = ({ data, isFetching, setEditMode, uid }) => {
  const { role } = useAuth()
  const [updateUser, { isLoading }] = useUpdateUserMutation()

  const [firstName, setFirstName] = useState(data.firstName || "")
  const [lastName, setLastName] = useState(data.lastName || "")
  const [dob, setDob] = useState(data.dob || "")
  const [streetAddress, setStreetAddress] = useState(data.address?.streetAddress || "")
  const [city, setCity] = useState(data.address?.city || "")
  const [county, setCounty] = useState(data.address?.county || "")
  const [postcode, setPostcode] = useState(data.address?.postcode || "")
  const [country, setCountry] = useState(data.address?.country || "")
  const [phoneNumber, setPhoneNumber] = useState(data.phoneNumber || "")

  const [firstNameValid, setFirstNameValid] = useState(true)
  const [lastNameValid, setLastNameValid] = useState(true)
  const [dobValid, setDobValid] = useState(true)
  const [streetAddressValid, setStreetAddressValid] = useState(true)
  const [cityValid, setCityValid] = useState(true)
  const [countyValid, setCountyValid] = useState(true)
  const [postcodeValid, setPostcodeValid] = useState(true)
  const [countryValid, setCountryValid] = useState(true)
  const [phoneNumberValid, setPhoneNumberValid] = useState(true)

  const [error, setError] = useState("")

  const edited = [
    firstName.trim() !== data.firstName,
    lastName.trim() !== data.lastName,
    dob !== data.dob,
    streetAddress.trim() !== data.address?.streetAddress,
    city.trim() !== data.address?.city,
    county.trim() !== data.address?.county,
    postcode.trim() !== data.address?.postcode,
    country.trim() !== data.address?.country,
    phoneNumber.trim() !== data.phoneNumber,
  ].some(Boolean)

  const allValid = [
    firstNameValid,
    lastNameValid,
    dobValid,
    streetAddressValid,
    cityValid,
    countyValid,
    postcodeValid,
    countryValid,
    phoneNumberValid,
  ].every(Boolean)

  const allFields = [
    firstName,
    lastName,
    dob,
    streetAddress,
    city,
    county,
    postcode,
    country,
    phoneNumber,
  ].every(Boolean)

  useEffect(() => {
    if (allValid && error) setError("")
  }, [allValid, error])

  useEffect(() => {
    if (error) {
      if (allFields && error === "All fields are required") setError("")
      if (allValid && error === "All fields must be valid") setError("")
      if (!allFields && error === "All fields must be valid") setError("All fields are required")
      if (!allValid && error === "All fields are required") setError("All fields must be valid")
    }
  }, [allValid, allFields])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isLoading || isFetching) return

    if (!allFields) return setError("All fields are required")
    if (!allValid) return setError("All fields must be valid")

    try {
      if (edited) {
        const updateData = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dob,
          address: {
            streetAddress: streetAddress.trim(),
            city: city.trim(),
            county: county.trim(),
            postcode: postcode.toUpperCase().trim(),
            country: country.trim(),
          },
          phoneNumber: phoneNumber.trim(),
          club: data.club || null,
          role
        }
        // Add target users uid to updateData if user is admin
        if (role === "admin") updateData.uid = uid

        await updateUser(updateData).unwrap()
      }

      toast.success("Profile updated")

      if (setEditMode) setEditMode()

    } catch (err) {
      console.error("Failed to update user", err)
      toast.error("Failed to update user")
    }
  }

  const handleEditClicked = () => {
    setEditMode((state) => !state)
  }

  return (
    <>
      <Form
        className="w-100 mx-auto bg-white shadow-sm rounded-4 p-4"
        onSubmit={handleSubmit}
      >
        <div className='d-flex justify-content-between align-items-center'>
          <h4 className="my-3 fw-bold ps-2">Basic Details</h4>
          {role === "admin" && (
            <ButtonWithTooltip text="Edit" className="border-0" action={setEditMode}>
              <Button
                onClick={handleEditClicked}
                className="me-2 no-arrow d-flex align-items-center justify-content-center rounded-circle p-2 bg-white shadow-btn border"
                variant=''
              >
                <Edit />
              </Button>
            </ButtonWithTooltip>
          )}
        </div>

        <div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">First Name</b>
            <div className="w-100">
              <ControlInput
                isValid={firstNameValid}
                setValid={setFirstNameValid}
                setValue={setFirstName}
                validTest={isValidName}
                value={firstName}
                type="text"
                name="first name"
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Last Name</b>
            <div className="w-100">
              <ControlInput
                isValid={lastNameValid}
                setValid={setLastNameValid}
                setValue={setLastName}
                validTest={isValidName}
                value={lastName}
                type="text"
                name="last name"
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Date of Birth</b>
            <div className="w-100">
              <ControlInput
                isValid={dobValid}
                setValid={setDobValid}
                setValue={setDob}
                validTest={isValidDob}
                value={dob}
                type="date"
                name="date of birth"
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row  border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Address</b>
            <div className="w-100 d-flex flex-column gap-2">
              <ControlInput
                isValid={streetAddressValid}
                setValid={setStreetAddressValid}
                setValue={setStreetAddress}
                validTest={isValidAddress}
                value={streetAddress}
                type="text"
                as="textarea"
                rows={1}
                name="street address"
              />
              <ControlInput
                isValid={cityValid}
                setValid={setCityValid}
                setValue={setCity}
                validTest={isValidCityOrCounty}
                value={city}
                type="text"
                name="town or city"
              />
              <ControlInput
                isValid={countyValid}
                setValid={setCountyValid}
                setValue={setCounty}
                validTest={isValidCityOrCounty}
                value={county}
                type="text"
                name="county or state"
              />
              <ControlInput
                isValid={postcodeValid}
                setValid={setPostcodeValid}
                setValue={setPostcode}
                validTest={isValidPostcode}
                value={postcode}
                type="text"
                name="postcode"
              />
              <ControlInput
                isValid={countryValid}
                setValid={setCountryValid}
                setValue={setCountry}
                validTest={isValidCountry}
                value={country}
                type="text"
                name="country"
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Phone Number</b>
            <div className="w-100">
              <ControlInput
                isValid={phoneNumberValid}
                setValid={setPhoneNumberValid}
                setValue={setPhoneNumber}
                validTest={isValidPhoneNumber}
                value={phoneNumber}
                type="tel"
                name="phone number"
              />
            </div>
          </div>
        </div>

        {error && <p className="text-danger i text-center fw-semibold">{error}</p>}
        <Button
          className="px-5 mt-3 mx-auto d-block rounded-3"
          variant="dark"
          type="submit"
          disabled={isLoading}
        >
          <LoadingTextSwap isLoading={isLoading}>
            Save
          </LoadingTextSwap>
        </Button>
      </Form>
    </>
  )
}
export default UpdateUserForm